/**
 * Advanced Analytics page — competency radar chart, readiness score,
 * and per-company breakdowns.
 *
 * Shows:
 * - Interview Readiness gauge (weighted composite score)
 * - Competency Radar Chart (6 STAR dimensions)
 * - Per-company score breakdown table
 *
 * All data comes from a single /api/v1/dashboard/analytics endpoint
 * to minimise roundtrips. Requires authentication.
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import {
  getAnalytics,
  DimensionAverage,
  CompanyBreakdown,
  ReadinessScore,
} from "../api/client";
import PageLoader from "../components/PageLoader";

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function Analytics() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("pages-core");

  // Chart colours — consistent with Dashboard.tsx
  const DIMENSION_COLORS: Record<string, string> = {
    Situation: theme.palette.success.main,
    Task: theme.palette.info.main,
    Action: theme.palette.warning.main,
    Result: theme.palette.primary.main,
    Engagement: theme.palette.error.main,
    Overall: theme.palette.text.primary,
  };

  const {
    data: analytics,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["analytics"],
    queryFn: getAnalytics,
    staleTime: 60_000,
  });

  if (isLoading) {
    return <PageLoader message={t("Analytics.loadingMessage")} />;
  }

  if (error || !analytics) {
    return (
      <Alert severity="error">
        {t("Analytics.loadErrorMessage")}
      </Alert>
    );
  }

  const hasData = analytics.dimension_averages.some((d) => d.count > 0);

  return (
    <Box sx={{ maxWidth: 960, mx: "auto" }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            fontWeight={500}
            sx={{ fontSize: { xs: "1.5rem", sm: "2.125rem" } }}
          >
            {t("Analytics.pageTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("Analytics.pageSubtitle")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
        >
          {t("Analytics.dashboardButton")}
        </Button>
      </Stack>

      {!hasData ? (
        <Card>
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t("Analytics.noDataTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("Analytics.noDataBody")}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/evaluate")}
            >
              {t("Analytics.startFirstEvaluationButton")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {/* Readiness Score */}
          <Grid item xs={12} md={4}>
            <ReadinessGauge readiness={analytics.readiness} />
          </Grid>

          {/* Radar Chart */}
          <Grid item xs={12} md={8}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={500}>
                  {t("Analytics.competencyProfileTitle")}
                </Typography>
                <CompetencyRadar dimensions={analytics.dimension_averages} />
              </CardContent>
            </Card>
          </Grid>

          {/* Dimension Breakdown Bars */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={500}>
                  {t("Analytics.dimensionBreakdownTitle")}
                </Typography>
                <DimensionBars dimensions={analytics.dimension_averages} dimensionColors={DIMENSION_COLORS} />
              </CardContent>
            </Card>
          </Grid>

          {/* Per-Company Breakdown */}
          {analytics.company_breakdowns.length > 0 && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight={500}>
                    {t("Analytics.performanceByCompanyTitle")}
                  </Typography>
                  <CompanyTable breakdowns={analytics.company_breakdowns} />
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Readiness Gauge
// ---------------------------------------------------------------------------

function ReadinessGauge({
  readiness,
}: {
  readiness: ReadinessScore | null;
}) {
  const { t } = useTranslation("pages-core");

  if (!readiness) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            {t("Analytics.readinessGaugeNeedMoreDataMessage")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const gaugeColor =
    readiness.overall_readiness >= 80
      ? "success"
      : readiness.overall_readiness >= 60
      ? "secondary"
      : readiness.overall_readiness >= 40
      ? "warning"
      : "error";

  const components = [
    { labelKey: "Analytics.readinessComponentScoreQuality", value: readiness.score_component, weight: "40%" },
    {
      labelKey: "Analytics.readinessComponentConsistency",
      value: readiness.consistency_component,
      weight: "30%",
    },
    {
      labelKey: "Analytics.readinessComponentImprovementTrend",
      value: readiness.trend_component,
      weight: "30%",
    },
  ];

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight={500}>
          {t("Analytics.interviewReadinessTitle")}
        </Typography>

        {/* Central score */}
        <Box sx={{ textAlign: "center", my: 2 }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={readiness.overall_readiness}
              size={120}
              thickness={5}
              color={gaugeColor as any}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography variant="h4" fontWeight={500}>
                {readiness.overall_readiness}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("Analytics.readinessScoreOutOf100")}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="subtitle1"
            fontWeight={500}
            color={`${gaugeColor}.main`}
            sx={{ mt: 1 }}
          >
            {readiness.label}
          </Typography>
        </Box>

        {/* Component breakdown */}
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {components.map((c) => (
            <Box key={c.labelKey}>
              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mb: 0.5 }}
              >
                <Typography variant="caption" fontWeight={500}>
                  {t(c.labelKey)}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                  >
                    {" "}
                    ({c.weight})
                  </Typography>
                </Typography>
                <Typography variant="caption" fontWeight={500}>
                  {c.value}%
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={c.value}
                sx={{ height: 6, borderRadius: 3 }}
                color={
                  c.value >= 70
                    ? "success"
                    : c.value >= 50
                    ? "warning"
                    : "error"
                }
              />
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Competency Radar Chart
// ---------------------------------------------------------------------------

function CompetencyRadar({
  dimensions,
}: {
  dimensions: DimensionAverage[];
}) {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const radarData = dimensions.map((d) => ({
    dimension: d.dimension,
    score: d.average,
    fullMark: 5,
  }));

  // Recharts v3 Polar* components have a ReactNode return type that clashes
  // with React 19's stricter JSX element types. Cast to any to fix.
  const AngleAxis = PolarAngleAxis as any;
  const RadiusAxis = PolarRadiusAxis as any;

  return (
    <ResponsiveContainer width="100%" height={300}>
      <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
        <PolarGrid stroke={theme.palette.divider} />
        <AngleAxis
          dataKey="dimension"
          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
        />
        <RadiusAxis
          angle={90}
          domain={[0, 5]}
          tick={{ fontSize: 10 }}
          tickCount={6}
        />
        <Tooltip
          formatter={(value: any) => [Number(value).toFixed(1), t("Analytics.radarTooltipAvgScoreLabel")]}
        />
        <Radar
          name={t("Analytics.radarYourScoresLabel")}
          dataKey="score"
          stroke={theme.palette.text.primary}
          fill={theme.palette.text.primary}
          fillOpacity={0.25}
          strokeWidth={2}
          dot={{ r: 4, fill: theme.palette.text.primary }}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Dimension Breakdown Bars
// ---------------------------------------------------------------------------

function DimensionBars({
  dimensions,
  dimensionColors,
}: {
  dimensions: DimensionAverage[];
  dimensionColors: Record<string, string>;
}) {
  const theme = useTheme();
  const { t } = useTranslation("pages-core");
  const barData = dimensions.map((d) => ({
    dimension: d.dimension,
    average: d.average,
    fill: dimensionColors[d.dimension] || theme.palette.text.primary,
  }));

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={barData}
        layout="vertical"
        margin={{ top: 5, right: 30, left: 60, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
        <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 12 }} />
        <YAxis
          type="category"
          dataKey="dimension"
          tick={{ fontSize: 12 }}
          width={80}
        />
        <Tooltip formatter={(value: any) => [Number(value).toFixed(1), t("Analytics.barTooltipAvgLabel")]} />
        <Bar
          dataKey="average"
          radius={[0, 4, 4, 0]}
          barSize={24}
          label={{
            position: "right",
            fontSize: 12,
            fontWeight: 500,
            formatter: (value: any) => Number(value).toFixed(1),
          }}
        >
          {barData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.fill} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------------------
// Per-Company Table
// ---------------------------------------------------------------------------

function scoreColor(score: number | null): string {
  if (score === null) return "text.secondary";
  if (score >= 4) return "success.main";
  if (score >= 3) return "warning.main";
  return "error.main";
}

function CompanyTable({
  breakdowns,
}: {
  breakdowns: CompanyBreakdown[];
}) {
  const { t } = useTranslation("pages-core");
  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ minWidth: 600 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 500 }}>{t("Analytics.companyColumnHeader")}</TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.evalsColumnHeader")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.avgColumnHeader")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.bestColumnHeader")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.situationColumnHeader")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.taskColumnHeader")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.actionColumnHeader")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.resultColumnHeader")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.engagementColumnHeader")}
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 500 }}>
              {t("Analytics.overallColumnHeader")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {breakdowns.map((b) => (
            <TableRow key={b.company_name} hover>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {b.company_name}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip
                  label={b.evaluation_count}
                  size="small"
                  variant="outlined"
                  sx={{ height: 22, fontSize: 11 }}
                />
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={scoreColor(b.average_score)}
                >
                  {b.average_score?.toFixed(1) ?? "—"}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={scoreColor(b.best_score)}
                >
                  {b.best_score?.toFixed(1) ?? "—"}
                </Typography>
              </TableCell>
              {[
                b.situation_avg,
                b.task_avg,
                b.action_avg,
                b.result_avg,
                b.engagement_avg,
                b.overall_avg,
              ].map((score, i) => (
                <TableCell key={i} align="center">
                  <Typography
                    variant="body2"
                    color={scoreColor(score)}
                  >
                    {score?.toFixed(1) ?? "—"}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
